import { useEffect, useLayoutEffect, useRef } from 'preact/hooks'
import type { UiIntent } from '#contract'
import { useClient } from './client-context.ts'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Menu navigation from the gamepad (and Esc as `back`). DOM focus stays the single source
 * of truth: up/down move it through the active screen, confirm clicks it, left/right send
 * arrow keys to it. `onBack` decides what back means where the hook is used.
 */
export function useUiIntents(onBack: () => void): void {
  const client = useClient()
  const onBackRef = useRef(onBack)
  // Layout effects run right after the render, before paint. An effect could run frames
  // later, and a fast back after a screen change would reach the previous screen's handler.
  useLayoutEffect(() => {
    onBackRef.current = onBack
  })

  useEffect(() => {
    if (!client) return
    return client.onUiIntent((intent) => {
      if (intent === 'back') onBackRef.current()
      // The inventory screen's own; it handles them itself (`useGridFocus`).
      else if (intent !== 'alternate' && intent !== 'quick') navigate(intent)
    })
  }, [client])
}

function navigate(intent: Exclude<UiIntent, 'back' | 'alternate' | 'quick'>): void {
  const screen = [...document.querySelectorAll<HTMLElement>('[data-screen]')].at(-1)
  if (!screen) return
  const targets = [...screen.querySelectorAll<HTMLElement>(FOCUSABLE)]
  const active = document.activeElement
  const focused = active instanceof HTMLElement && screen.contains(active) ? active : null

  if (intent === 'up' || intent === 'down') {
    const step = intent === 'down' ? 1 : -1
    const index = focused ? targets.indexOf(focused) : -1
    const next = index === -1 ? (step > 0 ? 0 : targets.length - 1) : index + step
    targets.at(next % targets.length)?.focus({ focusVisible: true })
  } else if (intent === 'confirm') {
    if (focused) focused.click()
    else targets[0]?.focus({ focusVisible: true })
  } else {
    // Synthetic events don't run the browser's default action (a native range input won't
    // step), so controls that use left/right handle `keydown` themselves.
    const key = intent === 'left' ? 'ArrowLeft' : 'ArrowRight'
    focused?.dispatchEvent(new KeyboardEvent('keydown', { key, code: key, bubbles: true }))
  }
}
